import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { OtaService } from 'src/app/Core/services/ota.service';
import { ToasterService } from 'src/app/Core/services/toaster.service';
import { UserService } from 'src/app/Core/services/user.service';
import { OtaDetailComponent, OtaDetailDialogResult } from '../ota-detail/ota-detail.component';

export interface DropdownOption {
  label: string;
  value: string;
}

export interface WmUpgrade {
  file_name: string;
  firmware_version: string;
  file_path_on_device: string;
  description: string;
  priority: number;
}

export interface MbUpgrade {
  file_name: string;
  firmware_version: string;
  file_path_on_device: string;
  description: string;
  priority: number;
}

export interface ExponentialRate {
  baseRatePerMinute: number;
  incrementFactor: number;
  rateIncreaseCriteria: {
    numberOfSucceededThings: number;
  };
}

export interface JobExecutionsRolloutConfig {
  exponentialRate: ExponentialRate;
  maximumPerMinute: number;
}

export interface AbortCriteria {
  action: string;
  failureType: string;
  minNumberOfExecutedThings: number;
  thresholdPercentage: number;
}

export interface AbortConfig {
  criteriaList: AbortCriteria[];
}

export interface OTAJob {
  id: number;
  job_name: string;
  model: string;
  job_type: string;
  upgrade_type: string;
  created_at: string;
  status: string;
  wm_upgrade?: WmUpgrade;
  mb_upgrade?: MbUpgrade;
  job_executions_rollout_config: JobExecutionsRolloutConfig;
  abort_config: AbortConfig;
  regions: string[];
  inprogress_timeout_in_minutes: number;
}

@Component({
  selector: 'app-ota-dashboard',
  templateUrl: './ota-dashboard.component.html',
  styleUrls: ['./ota-dashboard.component.css']
})
export class OtaDashboardComponent implements OnInit {
  jobs: OTAJob[] = [];
  filteredJobs: OTAJob[] = [];
  totalRecords = 10;
  itemsPerPage = 5;
  currentPage = 1;
  totalPages = Math.ceil(this.totalRecords / this.itemsPerPage);
  searchQuery = '';
  selectedFilterValue = 'job_name';

  // Dropdown options
  filterOptions: DropdownOption[] = [
    { label: 'Job Name', value: 'job_name' },
    { label: 'Model', value: 'model' }
  ];

  activeFilters: { [key: string]: boolean } = {};
  availableModels: string[] = [];

  constructor(private userService: UserService, private otaService: OtaService, private toaster: ToasterService, private router: Router, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.fetchOTAJobs();
    this.totalRecords = this.jobs.length;
  }

  fetchOTAJobs() {
    this.otaService.getOTAJobs().subscribe({
      next: (data: OTAJob[]) => {
        this.jobs = [...data].sort((a: OTAJob, b: OTAJob) => {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        this.totalRecords = data.length;
        this.initializeDynamicFilters();
        this.applyFilters();
      },
      error: (error: any) => {
        console.error('Error loading jobs:', error);
        if (error instanceof HttpErrorResponse) {
          this.toaster.openDialogBox(error.error.message, false);
        }
      }
    });
  }

  /**
   * Initialize dynamic filters based on unique models from API response
   */
  private initializeDynamicFilters(): void {
    // Get unique models from jobs
    const modelsSet = new Set(this.jobs.map(job => job.model));
    this.availableModels = Array.from(modelsSet).sort();

    // Initialize activeFilters object with all models set to false
    this.activeFilters = {};
    this.availableModels.forEach(model => {
      this.activeFilters[model] = false;
    });

    console.log('Available Models:', this.availableModels);
    console.log('Active Filters:', this.activeFilters);
  }

  onFilterSelectionChange(value: string): void {
    this.selectedFilterValue = value;
    this.currentPage = 1;
    this.applyFilters();
    console.log('Selected filter:', value);
  }

  applyFilters(): void {
    let filtered: OTAJob[] = [...this.jobs];

    // Check if any model filter is active
    const hasActiveModelFilter = Object.values(this.activeFilters).some(value => value === true);

    // Apply model filters
    if (hasActiveModelFilter) {
      filtered = filtered.filter(job => this.activeFilters[job.model] === true);
    }

    // Apply search filter
    if (this.searchQuery.trim()) {
      filtered = filtered.filter(job => {
        if (this.selectedFilterValue === 'model') {
          return job.model.toLowerCase().includes(this.searchQuery.toLowerCase());
        } else {
          return job.job_name.toLowerCase().includes(this.searchQuery.toLowerCase());
        }
      });
    }

    this.filteredJobs = filtered;
    this.totalPages = Math.ceil(this.filteredJobs.length / this.itemsPerPage);

    // Reset to page 1 if current page exceeds total pages
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = 1;
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  toggleFilter(filter: keyof typeof this.activeFilters): void {
    this.activeFilters[filter] = !this.activeFilters[filter];
    this.currentPage = 1;
    this.applyFilters();
  }

  clearAllFilters(): void {
    this.activeFilters = {
      success: false,
      cancelled: false,
      morpheus: false,
      athena: false
    };
    this.searchQuery = '';
    this.selectedFilterValue = 'job_id';
    this.currentPage = 1;
    this.applyFilters();
  }

  openJobDetail(job: OTAJob): void {
    const dialogRef = this.dialog.open(OtaDetailComponent, {
      data: job,
      width: '80vw',
      maxWidth: '1200px',
      height: '80vh',
      panelClass: 'ota-detail-dialog-panel'
    });

    dialogRef.afterClosed().subscribe((result?: OtaDetailDialogResult) => {
      if (!result) {
        return;
      }
      if (result.action === 'delete') {
        this.currentPage = 1;
      }
      this.fetchOTAJobs();
    });
  }

  createJob(): void {
    console.log('Create a new job');
    // Implement job creation logic
    this.router.navigate(['/create-ota-job']);
  }

  getPaginatedJobs(): OTAJob[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredJobs.slice(startIndex, endIndex);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  getPageNumbers(): number[] {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  get startItem(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get endItem(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
  }

  formatStatus(status: string): 'Success' | 'Cancelled' | 'In Progress' | 'Rejected' | 'Queued' | 'Deleted' {
    switch (status) {
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'SUCCEEDED':
        return 'Success';
      case 'CANCELLED':
        return 'Cancelled';
      case 'REJECTED':
        return 'Rejected';
      case 'QUEUED':
        return 'Queued';
      case 'DELETED':
        return 'Deleted';
      default:
        return status as any;
    }
  }

  /**
   * Get active filter keys (models that are selected)
   */
  getActiveFilterKeys(): string[] {
    return Object.keys(this.activeFilters).filter(key => this.activeFilters[key] === true);
  }

  /**
   * Get count of active filters
   */
  getActiveFiltersCount(): number {
    return this.getActiveFilterKeys().length;
  }
}